import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import {
  Sparkles, RefreshCw, Loader2, ArrowRight, TrendingUp, TrendingDown, Minus,
  Heart, AlertTriangle, CheckCircle, XCircle, Zap, CalendarDays,
} from 'lucide-react';
import { healthScoreApi } from '../lib/api';
import { toast } from 'sonner';
import { cn, formatDateTime } from '../lib/utils';
import { RoadmapDialog } from './RoadmapDialog';

const LEVEL_CFG = {
  critical: { label: 'Crítico', bg: 'from-red-500 to-rose-600', text: 'text-red-700', ring: 'ring-red-400', icon: XCircle, ringColor: '#ef4444' },
  warning: { label: 'Em risco', bg: 'from-amber-500 to-orange-600', text: 'text-amber-700', ring: 'ring-amber-400', icon: AlertTriangle, ringColor: '#f59e0b' },
  good: { label: 'Bom', bg: 'from-blue-500 to-indigo-600', text: 'text-blue-700', ring: 'ring-blue-400', icon: CheckCircle, ringColor: '#3b82f6' },
  excellent: { label: 'Excelente', bg: 'from-emerald-500 to-green-600', text: 'text-emerald-700', ring: 'ring-emerald-400', icon: Zap, ringColor: '#10b981' },
};

const PRIORITY_CFG = {
  high: { color: 'bg-red-50 text-red-700 border-red-200', label: 'Alta' },
  medium: { color: 'bg-amber-50 text-amber-700 border-amber-200', label: 'Média' },
  low: { color: 'bg-blue-50 text-blue-700 border-blue-200', label: 'Baixa' },
};

const TREND_ICON = { up: TrendingUp, down: TrendingDown, stable: Minus };
const TREND_COLOR = { up: 'text-green-600', down: 'text-red-600', stable: 'text-slate-500' };

// Simple markdown bold/lists renderer
function md(s) {
  if (!s) return null;
  const parts = [];
  let last = 0; let idx = 0;
  const re = /\*\*([^*]+)\*\*/g; let m;
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) parts.push(s.slice(last, m.index));
    parts.push(<strong key={idx++} className="font-semibold">{m[1]}</strong>);
    last = m.index + m[0].length;
  }
  if (last < s.length) parts.push(s.slice(last));
  return parts;
}

export const HealthScoreWidget = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [roadmapOpen, setRoadmapOpen] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await healthScoreApi.get();
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao carregar Health Score');
    } finally { setLoading(false); }
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      const res = await healthScoreApi.refresh();
      setData(res.data);
      toast.success('Health Score recalculado');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao recalcular (~15s de espera)');
    } finally { setRefreshing(false); }
  };

  if (loading) {
    return (
      <Card className="card-flat bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-200" data-testid="health-score-loading">
        <CardContent className="p-6 flex items-center justify-center gap-3">
          <Sparkles className="h-5 w-5 text-indigo-600 animate-pulse" />
          <span className="text-sm text-indigo-700">A calcular Health Score com IA (~15s)...</span>
          <Loader2 className="h-4 w-4 animate-spin text-indigo-600" />
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  const cfg = LEVEL_CFG[data.level] || LEVEL_CFG.warning;
  const LevelIcon = cfg.icon;
  const score = data.score ?? 0;
  const circumference = 2 * Math.PI * 56;
  const dashOffset = circumference - (score / 100) * circumference;

  return (
    <Card className="card-flat overflow-hidden" data-testid="health-score-widget">
      <div className={cn('bg-gradient-to-br p-5 text-white', cfg.bg)}>
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="flex items-center gap-5">
            {/* Circular score */}
            <div className="relative shrink-0" data-testid="health-score-ring">
              <svg width="128" height="128" viewBox="0 0 128 128" className="-rotate-90">
                <circle cx="64" cy="64" r="56" fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="10" />
                <circle
                  cx="64" cy="64" r="56" fill="none" stroke="white" strokeWidth="10" strokeLinecap="round"
                  strokeDasharray={circumference}
                  strokeDashoffset={dashOffset}
                  style={{ transition: 'stroke-dashoffset 1s ease-out' }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-4xl font-bold" data-testid="health-score-number">{score}</span>
                <span className="text-[10px] uppercase tracking-widest opacity-80">de 100</span>
              </div>
            </div>
            <div className="min-w-0 max-w-2xl">
              <div className="flex items-center gap-2 mb-1.5">
                <Sparkles className="h-4 w-4" />
                <span className="text-[10px] font-semibold uppercase tracking-widest opacity-90">AI Compliance Health Score</span>
              </div>
              <div className="flex items-center gap-2 mb-2">
                <LevelIcon className="h-5 w-5" />
                <h2 className="text-xl font-bold" data-testid="health-score-level">{cfg.label}</h2>
                {data.from_cache && (
                  <Badge variant="outline" className="bg-white/15 text-white border-white/30 text-[10px]">
                    Cache · {data.age_minutes}min
                  </Badge>
                )}
              </div>
              <p className="text-sm text-white/95 leading-relaxed" data-testid="health-score-summary">{md(data.summary)}</p>
            </div>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            <Button
              size="sm"
              onClick={() => setRoadmapOpen(true)}
              className="bg-white text-indigo-700 hover:bg-white/90 border-0 shadow-md font-semibold"
              data-testid="health-score-roadmap-btn"
            >
              <CalendarDays className="h-4 w-4 mr-1.5" />
              Gerar Plano de Ação 90 dias
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={refresh}
              disabled={refreshing}
              className="bg-white/15 hover:bg-white/25 text-white border-white/30"
              data-testid="health-score-refresh"
            >
              {refreshing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
              Recalcular
            </Button>
          </div>
        </div>
      </div>

      <CardContent className="p-5 space-y-5">
        {/* Components breakdown */}
        <div data-testid="health-score-components">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest mb-3">Componentes (ponderados)</p>
          <div className="space-y-2.5">
            {data.components.map((c, i) => {
              const TIcon = TREND_ICON[c.trend] || Minus;
              return (
                <div key={i} className="space-y-1" data-testid={`hs-component-${i}`}>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-medium text-slate-800 truncate">{c.name}</span>
                      <TIcon className={cn('h-3.5 w-3.5 shrink-0', TREND_COLOR[c.trend])} />
                      <span className="text-xs text-slate-500 shrink-0">peso {c.weight}%</span>
                    </div>
                    <span className={cn(
                      'text-sm font-semibold tabular-nums',
                      c.score >= 80 ? 'text-emerald-600' : c.score >= 60 ? 'text-blue-600' : c.score >= 40 ? 'text-amber-600' : 'text-red-600'
                    )}>{c.score}</span>
                  </div>
                  <Progress value={c.score} className="h-1.5" />
                  {c.comment && <p className="text-xs text-slate-500 ml-0.5">{c.comment}</p>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Top 3 actions */}
        <div data-testid="health-score-actions">
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Top 3 ações desta semana</p>
            <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200">
              <Sparkles className="h-2.5 w-2.5 mr-1" />Sugerido por IA
            </Badge>
          </div>
          <div className="space-y-2">
            {data.top_actions.map((a, i) => {
              const pCfg = PRIORITY_CFG[a.priority] || PRIORITY_CFG.medium;
              return (
                <button
                  key={i}
                  onClick={() => navigate(a.href)}
                  className="w-full text-left bg-slate-50 hover:bg-indigo-50 border border-slate-200 hover:border-indigo-300 rounded-lg px-4 py-3 transition-colors group"
                  data-testid={`hs-action-${i}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-xs font-bold text-slate-400 tabular-nums">#{i + 1}</span>
                        <Badge variant="outline" className={cn('text-[10px]', pCfg.color)}>{pCfg.label}</Badge>
                      </div>
                      <p className="text-sm font-medium text-slate-900 group-hover:text-indigo-700 transition-colors">{a.title}</p>
                      {a.why && <p className="text-xs text-slate-500 mt-0.5">{a.why}</p>}
                    </div>
                    <ArrowRight className="h-4 w-4 text-slate-400 group-hover:text-indigo-600 group-hover:translate-x-0.5 transition-all mt-0.5 shrink-0" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="text-[10px] text-slate-400 text-center pt-2 border-t border-slate-100">
          Calculado por Motor de IA • {data.computed_at ? formatDateTime(data.computed_at) : '—'} • Cache 30min
        </div>
      </CardContent>

      <RoadmapDialog open={roadmapOpen} onOpenChange={setRoadmapOpen} />
    </Card>
  );
};

export default HealthScoreWidget;
